import { PAGE_EXPERIMENT_PDP_NO_DEFAULT } from "@shared/utils.experimentation";
import type {
  ItemType,
  Price,
  MembershipPricingOption,
  PriceWithMembershipOptions
} from "@shared/graphql.types";

const {
  CONTROL,
  VARIANT_1,
  VARIANT_2,
  THROWAWAY_CONTROL,
  THROWAWAY_VARIANT_1,
  THROWAWAY_VARIANT_2
} = PAGE_EXPERIMENT_PDP_NO_DEFAULT.values;

export const shouldSetDefaultMembershipOption = (
  noDefaultPageExperimentVariant: number | undefined
): boolean | undefined => {
  switch (noDefaultPageExperimentVariant) {
    case VARIANT_1:
    case VARIANT_2:
    case THROWAWAY_VARIANT_1:
    case THROWAWAY_VARIANT_2: {
      return false;
    }
    default: {
      return true;
    }
  }
};

export const shouldRenderCTADisplayPrice = (
  noDefaultPageExperimentVariant: number | undefined,
  membershipSelected?: boolean
): boolean => {
  if (membershipSelected) {
    return true;
  }

  switch (noDefaultPageExperimentVariant) {
    case VARIANT_1:
    case VARIANT_2:
    case THROWAWAY_VARIANT_1:
    case THROWAWAY_VARIANT_2: {
      return false;
    }
    default: {
      return true;
    }
  }
};

export const atbWrapperVariant = (
  noDefaultPageExperimentVariant: number | undefined
) => {
  switch (noDefaultPageExperimentVariant) {
    case VARIANT_1:
    case THROWAWAY_VARIANT_1: {
      return "inline";
    }
    default: {
      return "adaptive";
    }
  }
};

export const membershipOptionsInView = (
  membershipOptionsRef: React.RefObject<HTMLDivElement>
) => {
  if (!membershipOptionsRef.current) return false;

  const rect = membershipOptionsRef.current.getBoundingClientRect();
  return rect.top >= 0 && rect.top <= window.innerHeight;
};

export const isNoDefaultExperiment = (
  noDefaultPageExperimentVariant: number | undefined
) => {
  switch (noDefaultPageExperimentVariant) {
    case CONTROL:
    case VARIANT_1:
    case VARIANT_2:
    case THROWAWAY_CONTROL:
    case THROWAWAY_VARIANT_1:
    case THROWAWAY_VARIANT_2: {
      return true;
    }
    default: {
      return false;
    }
  }
};

/**
 * Determines the user's pricing selection based on their basket contents
 * @param basket - User's basket items
 * @returns membership SKU if user selected member pricing, "GUEST" if guest pricing, null if basket empty
 */
export const getPricingSelectionFromBasket = (
  basket: ItemType[] | undefined
): string | null => {
  if (!basket || basket.length === 0) return null;

  // Check if basket has any membership item
  const membershipItem = basket.find((item) => item.type === "membership");

  if (membershipItem?.membership?.id) {
    return membershipItem.membership.id;
  }

  // If basket has products but no membership, user chose guest pricing
  const hasProducts = basket.some((item) => item.type === "product");
  if (hasProducts) {
    return "GUEST";
  }

  return null;
};

/**
 * Finds the price option that matches the user's pricing selection from basket
 * @param prices - Available price options for current product
 * @param pricingSelection - Result from getPricingSelectionFromBasket
 * @returns Matching price option or undefined
 */
export const findPriceOptionByPricingSelection = (
  prices: Price[],
  pricingSelection: string | null
): Price | undefined => {
  if (!pricingSelection) return undefined;

  if (pricingSelection === "GUEST") {
    // Find price option without membership (guest pricing)
    return prices.find((priceOption) => {
      const price = priceOption.price;
      // Guest pricing doesn't have membership or memberships fields
      return !("membership" in price) && !("memberships" in price);
    });
  }

  // Find price option with matching membership SKU
  return prices.find((priceOption) => {
    const price = priceOption.price;

    // Check single membership option
    if ("membership" in price) {
      return (
        (price as MembershipPricingOption).membership?.sku === pricingSelection
      );
    }

    // Check multiple membership options
    if ("memberships" in price) {
      return (price as PriceWithMembershipOptions).memberships.some(
        (membership) => membership.sku === pricingSelection
      );
    }

    return false;
  });
};
